import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../request/http-common';
import { setUser } from './userSlice';
import { setAuthStatus, setToken } from '../../request/Auth/authSlice';
import {
  Box, Typography, FormControl, OutlinedInput, InputLabel,
  InputAdornment, IconButton, Alert, Link
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, ArrowForward, Shield, TrendingUp, Speed } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const AuthInput = styled(OutlinedInput)(({ theme }) => ({
  borderRadius: '10px',
  fontSize: '15px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
    borderWidth: '1.5px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#F7A600',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#F7A600',
    borderWidth: '2px',
  },
}));

const SubmitButton = styled('button')(({ disabled }) => ({
  width: '100%',
  height: '52px',
  border: 'none',
  borderRadius: '10px',
  background: disabled ? 'rgba(247,166,0,0.4)' : 'linear-gradient(135deg, #F7A600 0%, #FFB52E 100%)',
  color: '#0a0d1a',
  fontWeight: 700,
  fontSize: '16px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'all 0.2s',
  boxShadow: disabled ? 'none' : '0 4px 20px rgba(247,166,0,0.35)',
  '&:hover': !disabled ? {
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 24px rgba(247,166,0,0.5)',
  } : {},
}));

const features = [
  { icon: <TrendingUp sx={{ fontSize: 20, color: '#F7A600' }} />, text: 'Institutional-grade trading tools' },
  { icon: <Shield sx={{ fontSize: 20, color: '#F7A600' }} />, text: 'Military-grade account security' },
  { icon: <Speed sx={{ fontSize: 20, color: '#F7A600' }} />, text: 'Sub-millisecond execution speed' },
];

const stats = [
  { value: '500K+', label: 'Active Clients' },
  { value: '$50B+', label: 'Volume Traded' },
  { value: '150+', label: 'Countries' },
];

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const appMode = useSelector((state) => state.mode.mode);
  const redirectTo = useSelector((state) => state.redirect.redirect);

  const [error, setErros] = useState('');
  const [registerForm, setRegisterForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);

  const onChange = (e) => setRegisterForm({ ...registerForm, [e.target.name]: e.target.value });

  const { isLoading: isSendingRequest, mutate: postLogin } = useMutation(
    async () => await apiClient.post('/api/login', { email: registerForm.email, password: registerForm.password }),
    {
      onSuccess: (res) => {
        localStorage.setItem('auth-token', res.data.access_token);
        dispatch(setUser(res.data.user));
        dispatch(setAuthStatus('authenticated'));
        dispatch(setToken(res.data.access_token));
        localStorage.setItem('notification', JSON.stringify({ message: `${res.data.message}`, type: 'success' }));
        window.dispatchEvent(new Event('processed'));
        window.dispatchEvent(new Event('newMessage'));
        if (redirectTo !== '') { navigate(redirectTo); } else { window.location.href = '/'; }
      },
      onError: (err) => {
        setErros(err.response?.data?.message || err.message);
        window.dispatchEvent(new Event('processed'));
      },
    }
  );

  const onSubmit = () => {
    window.dispatchEvent(new Event('processing'));
    postLogin();
  };

  const isDark = appMode === 'dark';

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: isDark ? '#080c18' : '#f1f5f9' }}>

      {/* ── Left Brand Panel ── */}
      <Box sx={{
        width: { xs: 0, lg: '45%' },
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: { lg: 5, xl: 7 },
        background: 'linear-gradient(160deg, #080c18 0%, #0d1526 55%, #111e35 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Ambient glows */}
        <Box sx={{ position: 'absolute', top: '-80px', right: '-80px', width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(247,166,0,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: '-60px', left: '-60px', width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(247,166,0,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '32px 32px', pointerEvents: 'none' }} />

        {/* Logo */}
        <Box>
          <img src="/intracapital-logos/intracapital-logo-white.png" alt="INTRA CAPITAL" style={{ height: '48px', marginBottom: '48px' }} />

          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ height: '2px', width: '28px', bgcolor: '#F7A600' }} />
            <Typography sx={{ color: '#F7A600', fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              Global Capital Markets
            </Typography>
          </Box>

          <Typography sx={{ fontSize: { lg: '36px', xl: '44px' }, fontWeight: 800, color: '#fff', lineHeight: 1.15, mb: 3 }}>
            Trade Smarter.<br />
            <Box component="span" sx={{ background: 'linear-gradient(90deg, #F7A600, #FFD166)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Grow Faster.
            </Box>
          </Typography>

          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px', lineHeight: 1.7, mb: 5, maxWidth: 360 }}>
            Access institutional-grade capital markets — equities, crypto, forex, and more — from a single, powerful platform.
          </Typography>

          {/* Feature list */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 6 }}>
            {features.map((f, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: 'rgba(247,166,0,0.1)', border: '1px solid rgba(247,166,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {f.icon}
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '14px', fontWeight: 500 }}>{f.text}</Typography>
              </Box>
            ))}
          </Box>

          {/* Stats */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 2 }}>
            {stats.map((s, i) => (
              <Box key={i} sx={{ p: 2, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', textAlign: 'center' }}>
                <Typography sx={{ color: '#F7A600', fontWeight: 800, fontSize: '20px', lineHeight: 1 }}>{s.value}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', mt: 0.5 }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Bottom copyright */}
        <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '12px' }}>
          © {new Date().getFullYear()} INTRA CAPITAL LIMITED. All rights reserved.
        </Typography>
      </Box>

      {/* ── Right Form Panel ── */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 3, sm: 5 },
        bgcolor: isDark ? '#0d1117' : '#ffffff',
        overflowY: 'auto',
      }}>
        <Box sx={{ width: '100%', maxWidth: '420px' }}>

          {/* Mobile logo */}
          <Box sx={{ display: { xs: 'block', lg: 'none' }, textAlign: 'center', mb: 5 }}>
            <img src={isDark ? '/intracapital-logos/intracapital-logo-white.png' : '/intracapital-logos/intracapital-logo.png'} alt="INTRA CAPITAL" style={{ height: '44px' }} />
          </Box>

          {/* Heading */}
          <Box sx={{ mb: 5 }}>
            <Typography sx={{ fontSize: '28px', fontWeight: 800, color: isDark ? '#fff' : '#0f172a', mb: 1 }}>
              Welcome back
            </Typography>
            <Typography sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)', fontSize: '15px' }}>
              Sign in to your INTRA CAPITAL account
            </Typography>
          </Box>

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: '10px', fontSize: '14px' }}>
              {error}
            </Alert>
          )}

          {/* Fields */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ '&.Mui-focused': { color: '#F7A600' } }}>Email Address</InputLabel>
              <AuthInput
                error={!!error}
                onChange={onChange}
                id="email"
                type="email"
                name="email"
                label="Email Address"
                startAdornment={
                  <InputAdornment position="start">
                    <Email sx={{ color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', fontSize: '19px' }} />
                  </InputAdornment>
                }
              />
            </FormControl>

            <FormControl fullWidth>
              <InputLabel sx={{ '&.Mui-focused': { color: '#F7A600' } }}>Password</InputLabel>
              <AuthInput
                error={!!error}
                onChange={onChange}
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                label="Password"
                startAdornment={
                  <InputAdornment position="start">
                    <Lock sx={{ color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', fontSize: '19px' }} />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
          </Box>

          {/* Links row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
            <Link component={RouterLink} to="/forgot" underline="none" sx={{ color: '#F7A600', fontSize: '14px', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>
              Forgot password?
            </Link>
            <Link component={RouterLink} to="/register" underline="none" sx={{ color: '#F7A600', fontSize: '14px', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>
              Create account
            </Link>
          </Box>

          {/* Submit */}
          <SubmitButton onClick={onSubmit} disabled={isSendingRequest}>
            {isSendingRequest ? 'Signing in…' : 'Sign In'}
            {!isSendingRequest && <ArrowForward sx={{ fontSize: 18 }} />}
          </SubmitButton>

          {/* Footer */}
          <Typography sx={{ textAlign: 'center', mt: 4, fontSize: '13px', color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.35)', lineHeight: 1.6 }}>
            By signing in you agree to our{' '}
            <Link href="https://intracapitals.com/terms-of-service" target="_blank" rel="noopener noreferrer" underline="none" sx={{ color: '#F7A600', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link href="https://intracapitals.com/privacy-policy" target="_blank" rel="noopener noreferrer" underline="none" sx={{ color: '#F7A600', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>
              Privacy Policy
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
